import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import moment from 'moment';
import Datepicker from '@mos-cat/datepicker';

import MosSelect from '@mos-front-components/public/es/components/MosSelect';
import MaskedField from '../MaskedField/MaskedField';
import TextField from '../TextField/TextField';
import Message, {
  TYPE as MESSAGE_TYPE,
} from '@mos-front-components/public/es/components/Message';
import '@mos-front-components/public/es/components/PguFormGrid/PguFormGrid.pcss';

import {
  FIELD_NAME,
  SELECT_OPTIONS_TEXT,
  TRANSLATED_DOCUMENTS,
} from './private/constants';
import {
  CITIZENSHIP_TYPE,
  DOCUMENT_TYPE,
  DOCUMENTS_BY_CITIZENSHIP,
} from '../../constants/citizenship';
import {
  birthCertificateRfMaskOptions,
  maskOptions,
  passporRfMaskOptions,
} from '../../helpers/maskOptions';
import { EMOJI_REGEXP } from '../../constants/regexp';
import { DATE_FORMAT } from '../../helpers/date';

const { ERROR_LOCATION, POSITIONS } = Datepicker;

/* eslint-disable no-useless-escape*/

const noop = () => {};

class Passport extends PureComponent {
  constructor(props) {
    super(props);
    this.byWhomFieldRef = React.createRef();
  }

  static findDocumentTypeBySelectValue(value) {
    if (!value) {
      return '';
    }

    const [documentType] = Object.entries(SELECT_OPTIONS_TEXT).find(
      ([_, val]) => value === val,
    );

    return documentType;
  }

  static findValueByDocumentType(value) {
    const [_, documentValue] = Object.entries(SELECT_OPTIONS_TEXT).find(
      ([key]) => value === key,
    );

    return documentValue;
  }

  static getShowTranslateNotice(selectValue) {
    return TRANSLATED_DOCUMENTS.includes(
      Passport.findDocumentTypeBySelectValue(selectValue),
    );
  }

  handleChange = (field, value) => {
    const { onChange, data } = this.props;

    const strippedValue = value.replace(EMOJI_REGEXP, '');

    if (data[field] !== strippedValue) {
      const modifiedData = { ...data, [field]: strippedValue };
      onChange(modifiedData, field);
    }
  };

  handleDocumentTypeChange = (selectValue) => {
    const { documentType } = this.props;

    const willCountFieldsChange =
      documentType === DOCUMENT_TYPE.PASSPORT_RF ||
      selectValue.text === DOCUMENT_TYPE.PASSPORT_RF;

    const newDocumentType = Passport.findDocumentTypeBySelectValue(
      selectValue.text,
    );
    this.props.onDocumentTypeChange(newDocumentType, willCountFieldsChange);
  };

  handleBlur = (field) => (event) => {
    this.props.onBlur(field, event);
    this.props.saveDraft();
  };

  renderPassportOtherField = () => {
    const {
      data: { byWhom },
      errors,
      documentType,
      disabled,
      editableFields,
      placeholders,
    } = this.props;

    if (documentType === DOCUMENT_TYPE.PASSPORT_RF) {
      return (
        <div className="PguFormGrid__row">
          <div className="PguFormGrid_col PguFormGrid__col_3_2">
            <TextField
              name="byWhom"
              label={placeholders.byWhom || 'Кем выдан'}
              className="PguFormGrid__field"
              onChange={(ev) =>
                this.handleChange(FIELD_NAME.byWhom, ev.currentTarget.value)
              }
              error={errors[FIELD_NAME.byWhom]}
              value={byWhom}
              disabled={disabled && !editableFields.includes(FIELD_NAME.byWhom)}
              onBlur={this.handleBlur(FIELD_NAME.byWhom)}
              maxLength="250"
              clear={() => {
                this.handleChange(FIELD_NAME.byWhom, '');
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="PguFormGrid__row">
        <div className="PguFormGrid_col PguFormGrid__col_5">
          <TextField
            name="byWhom"
            label="Кем выдан"
            className="PguFormGrid__field"
            onChange={(ev) =>
              this.handleChange(FIELD_NAME.byWhom, ev.currentTarget.value)
            }
            error={errors[FIELD_NAME.byWhom]}
            value={byWhom}
            disabled={disabled && !editableFields.includes(FIELD_NAME.byWhom)}
            ref={this.byWhomFieldRef}
            onBlur={this.handleBlur(FIELD_NAME.byWhom)}
            maxLength="250"
            clear={() => {
              this.handleChange(FIELD_NAME.byWhom, '');
            }}
          />
        </div>
      </div>
    );
  };

  render() {
    const {
      citizenship,
      data: { number, dateOfIssue },
      disabled,
      documentType,
      errors,
      permittedDocuments,
      editableFields,
      numberMask,
      placeholders,
      datePickerProps,
      saveDraft,
    } = this.props;

    if (!citizenship) {
      return null;
    }

    const {
      maxDate = moment().format(DATE_FORMAT),
      minDate = moment().subtract(130, 'years').format(DATE_FORMAT),
    } = datePickerProps;

    const documentTypeValue = documentType
      ? Passport.findValueByDocumentType(documentType)
      : '';
    const showTranslateNotice =
      Passport.getShowTranslateNotice(documentTypeValue);

    const selectOptions = permittedDocuments.map((type) => ({
      text: SELECT_OPTIONS_TEXT[type],
      value: SELECT_OPTIONS_TEXT[type],
    }));

    const selectValue = selectOptions.find(
      (option) => option.text === documentTypeValue,
    )
      ? documentTypeValue
      : '';

    function getPassportMaskOptions(docType) {
      if (docType === DOCUMENT_TYPE.PASSPORT_RF) {
        return passporRfMaskOptions;
      }
      if (docType === DOCUMENT_TYPE.BIRTH_CERTIFICATE_RF) {
        return birthCertificateRfMaskOptions;
      }
      return {
        ...maskOptions,
        ...numberMask,
      };
    }

    return (
      <div className="Passport">
        <div className="PguFormGrid__row">
          <div className="PguFormGrid_col PguFormGrid__col_5">
            <MosSelect
              placeholder={
                placeholders.documentType || 'Документ, удостоверяющий личность'
              }
              value={selectValue || ''}
              suggestions={selectOptions}
              onChange={(e) => {
                this.handleDocumentTypeChange(e);
                saveDraft();
              }}
              disabled={disabled}
              hasError={errors.documentType}
              dropListView
            />
          </div>
        </div>
        {showTranslateNotice && (
          <div className="PguFormGrid__row">
            <div className="PguFormGrid_col PguFormGrid__col_5">
              <Message type={MESSAGE_TYPE.WARNING} smallerText>
                Вам необходимо указать сведения в соответствии с нотариально
                заверенным переводом
              </Message>
            </div>
          </div>
        )}
        {selectValue && (
          <div className="PguFormGrid__row">
            <div className="PguFormGrid_col PguFormGrid__col_3_2">
              <MaskedField
                name="passportNumber"
                label={placeholders.number || 'Серия и номер'}
                maskOptions={getPassportMaskOptions(documentType)}
                onChange={(ev) =>
                  this.handleChange(FIELD_NAME.number, ev.currentTarget.value)
                }
                error={errors[FIELD_NAME.number]}
                className="PguFormGrid__field"
                value={number}
                maxLength={50}
                disabled={
                  disabled && !editableFields.includes(FIELD_NAME.number)
                }
                onBlur={this.handleBlur('number')}
                clear={() => {
                  this.handleChange(FIELD_NAME.number, '');
                }}
              />
            </div>
            <div className="PguFormGrid_col PguFormGrid__col_3">
              <Datepicker
                {...datePickerProps}
                placeholder={placeholders.dateOfIssue || 'Дата выдачи'}
                value={dateOfIssue}
                onChange={({ target }) =>
                  this.handleChange('dateOfIssue', target.value)
                }
                size="STANDARD"
                error={errors[FIELD_NAME.dateOfIssue]}
                className="PguFormGrid__field"
                disabled={
                  disabled && !editableFields.includes(FIELD_NAME.dateOfIssue)
                }
                maxDate={maxDate}
                minDate={minDate}
                onBlur={this.handleBlur(FIELD_NAME.dateOfIssue)}
              />
            </div>
          </div>
        )}

        {selectValue && this.renderPassportOtherField()}
      </div>
    );
  }
}

/**
 *
 * @type {{number: string, dateOfIssue: string, byWhom: string}}
 */
Passport.FIELD_NAME = FIELD_NAME;

/**
 *
 * @type {{CITIZEN: string, FOREIGN_CITIZEN: string, NON_CITIZEN: string}}
 */
Passport.CITIZENSHIP_TYPE = CITIZENSHIP_TYPE;

/**
 * @type {Object}
 */
Passport.DOCUMENT_TYPE = DOCUMENT_TYPE;
Passport.DOCUMENTS_BY_CITIZENSHIP = DOCUMENTS_BY_CITIZENSHIP;
Passport.POSITIONS = POSITIONS;
Passport.ERROR_LOCATION = ERROR_LOCATION;

const errorPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.bool]);

Passport.propTypes = {
  onChange: PropTypes.func,
  data: PropTypes.shape({
    number: PropTypes.string,
    dateOfIssue: PropTypes.string,
    byWhom: PropTypes.string,
  }),
  /**
   * @type {Object<string, string|boolean>}
   */
  errors: PropTypes.shape({
    number: errorPropType,
    dateOfIssue: errorPropType,
    byWhom: errorPropType,
    documentType: errorPropType,
  }),
  citizenship: PropTypes.oneOf(Object.keys(Passport.CITIZENSHIP_TYPE)),
  documentType: PropTypes.oneOf([...Object.keys(Passport.DOCUMENT_TYPE), '']),
  /**
   * @type {Object<string, string[]>}
   */
  documentsByCitizenship: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.oneOf(Object.values(DOCUMENTS_BY_CITIZENSHIP))),
  ),
  permittedDocuments: PropTypes.arrayOf(
    PropTypes.oneOf(Object.keys(Passport.DOCUMENT_TYPE)),
  ),
  disabled: PropTypes.bool,
  onDocumentTypeChange: PropTypes.func,
  onBlur: PropTypes.func,
  editableFields: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(Passport.FIELD_NAME)),
  ),
  placeholders: PropTypes.shape({
    documentType: PropTypes.string,
    number: PropTypes.string,
    dateOfIssue: PropTypes.string,
    byWhom: PropTypes.string,
  }),
  /**
   * Настройки маски для номера.
   * Не перекрывает маску для Паспорта РФ, используется только для прочих документов.
   */
  numberMask: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      mask: PropTypes.string,
      regex: PropTypes.string,
      repeat: PropTypes.number,
      greedy: PropTypes.bool,
      onBeforePaste: PropTypes.func,
      definitions: PropTypes.object,
      placeholder: PropTypes.string,
      radixPoint: PropTypes.string,
      numericInput: PropTypes.bool,
    }),
  ]),
  datePickerProps: PropTypes.shape({
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    /**
     * Расположение ошибки Datepicker- placeholder, текст снизу или в Tooltip
     * Доступны как экспортируемые значения Passport.ERROR_LOCATION
     *
     * default ERROR_LOCATION.PLACEHOLDER
     */
    errorLocation: PropTypes.oneOf(Object.values(ERROR_LOCATION)),
    /**
     * Расположение Tooltip ошибки Datepicker
     * Доступны как экспортируемые значения Passport.POSITIONS
     *
     * default POSITIONS.TOP
     */
    tooltipPosition: PropTypes.oneOf(Object.values(POSITIONS)),
  }),
};

Passport.defaultProps = {
  onChange: noop,
  onDocumentTypeChange: noop,
  onBlur: noop,
  data: {
    number: '',
    dateOfIssue: '',
    byWhom: '',
  },
  errors: {
    number: false,
    dateOfIssue: false,
    byWhom: false,
    documentType: false,
  },
  citizenship: null,
  documentType: null,
  documentsByCitizenship: null,
  permittedDocuments: Object.values(Passport.DOCUMENT_TYPE),
  disabled: false,
  editableFields: [],
  placeholders: {},
  numberMask: undefined,
  datePickerProps: {
    errorLocation: Passport.ERROR_LOCATION.PLACEHOLDER,
    tooltipPosition: Passport.POSITIONS.TOP,
  },
};

export { DOCUMENT_TYPE, CITIZENSHIP_TYPE };

export default Passport;
